import React, { useMemo } from "react";
import PropTypes from "prop-types";
import styles from "./style.module.css";

export default function Pagination({
  totalItems,
  pageSize,
  currentPage,
  onChange,
  windowSize = 4,
}) {
  const totalPages = Math.max(1, Math.ceil((totalItems || 0) / Math.max(1, pageSize || 1)));
  const now = Math.min(totalPages, Math.max(1, Number(currentPage) || 1));
  const go = (p) => onChange(Math.min(totalPages, Math.max(1, Number(p))));

  const { innerPages, leftGap, rightGap } = useMemo(() => {
    const half = Math.floor(windowSize / 2);
    let start = Math.max(2, now - half);
    let end = Math.min(totalPages - 1, start + windowSize - 1);
    start = Math.max(2, Math.min(start, Math.max(2, totalPages - 1 - windowSize + 1)));
    end = Math.min(totalPages - 1, Math.max(start, start + windowSize - 1));

    const pages = [];
    for (let p = start; p <= end; p++) pages.push(p);

    return {
      innerPages: pages,
      leftGap: start > 2,
      rightGap: end < totalPages - 1,
    };
  }, [now, totalPages, windowSize]);

  if (totalPages <= 1) return null;

  return (
    <div className={styles.pagination} role="navigation" aria-label="Pagination">
      <button
        className={styles["pagination-prev"]}
        onClick={() => go(now - 1)}
        disabled={now <= 1}
        aria-label="Previous page"
      >
        &lt;
      </button>

      <div className={styles["pagination-body"]}>
        <span
          className={`${styles["pagination-number"]} ${
            now === 1 ? styles["pagination-number-on"] : ""
          }`}
          aria-current={now === 1 ? "page" : undefined}
          onClick={() => go(1)}
        >
          1
        </span>

        <div
          className={`${styles["pagination-line"]} ${
            leftGap ? styles["pagination-line-on"] : ""
          } ${leftGap && rightGap ? styles["pagination-line-mini"] : ""}`}
        />

        {innerPages.map((p) => (
          <span
            key={p}
            className={`${styles["pagination-number"]} ${
              now === p ? styles["pagination-number-on"] : ""
            }`}
            aria-current={now === p ? "page" : undefined}
            onClick={() => go(p)}
          >
            {p}
          </span>
        ))}

        {totalPages - 2 > windowSize && (
          <div
            className={`${styles["pagination-line"]} ${
              rightGap ? styles["pagination-line-on"] : ""
            } ${leftGap && rightGap ? styles["pagination-line-mini"] : ""}`}
          />
        )}

        {totalPages > 1 && (
          <span
            className={`${styles["pagination-number"]} ${
              now === totalPages ? styles["pagination-number-on"] : ""
            }`}
            aria-current={now === totalPages ? "page" : undefined}
            onClick={() => go(totalPages)}
          >
            {totalPages}
          </span>
        )}
      </div>

      <button
        className={styles["pagination-next"]}
        onClick={() => go(now + 1)}
        disabled={now >= totalPages}
        aria-label="Next page"
      >
        &gt;
      </button>
    </div>
  );
}

Pagination.propTypes = {
  totalItems: PropTypes.number.isRequired,
  pageSize: PropTypes.number.isRequired,
  currentPage: PropTypes.number.isRequired,
  onChange: PropTypes.func.isRequired,
  windowSize: PropTypes.number,
};